import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppsInProject } from '@/hooks/useApps';
import type { AutomationAppUIModel } from '@/types/automation';

interface CurrentAppResult {
  /** Location (Azure region) from URL params */
  locationName: string | undefined;
  /** App name from URL params — always defined inside app routes */
  appName: string | undefined;
  /** Project name from URL params */
  projectName: string | undefined;
  /** Full app object from the cached apps list */
  app: AutomationAppUIModel | undefined;
  /** True while the apps list is loading */
  isLoading: boolean;
  /** True when apps have loaded but this app wasn't found */
  notFound: boolean;
  /** The query error, if any */
  error: Error | null;
}

/**
 * URL-param-based app context hook.
 * Reads `appName` and `projectName` from React Router params and resolves
 * the full app object from the cached apps-in-project list.
 *
 * Replaces the old pattern of reading `selectedApp` from `useAppStore()`.
 */
export function useCurrentApp(): CurrentAppResult {
  const { locationName, projectName, appName } = useParams<{ tenantId: string; locationName: string; projectName: string; appName: string }>();
  const { data: apps, isLoading, error } = useAppsInProject(projectName ?? '');

  // Azure resource names are case-insensitive (case-preserving). Compare
  // case-insensitively so URL casing differences (recent-work links,
  // lowercased redirects, manually edited URLs) don't trigger a false
  // "Application Not Found" page.
  const app = useMemo(
    () => apps?.find((a) => a.name.toLowerCase() === appName?.toLowerCase()),
    [apps, appName],
  );

  const notFound = !isLoading && !!apps && !!appName && !app;

  return {
    locationName,
    projectName,
    appName,
    app,
    isLoading,
    notFound,
    error: error as Error | null,
  };
}
